import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from 'src/app/data.service';

/**
 *  ÁTSOROLÁS — tanuló másik osztályba, és ami vele jár.
 *
 *  Az átjelentkeztetés eddig két képernyő két műveletéből állt: az osztálybesorolásnál
 *  kivenni innen, betenni oda, majd a csoportbesorolásnál végigmenni a csoportokon.
 *  A második lépés maradt el rendszeresen — és nem is látszott, hogy elmaradt: a tanuló
 *  ott maradt a RÉGI osztályához tartozó csoportokban (napközi, bontott óra), és úgy
 *  szerepelt a névsorokban, mintha még az előző osztályba járna.
 *
 *  Itt a kettő egy műveletben megy, és a képernyő ELŐRE megmutatja, mi fog történni.
 *  A csoportokat nem soroljuk át magától: javaslatot teszünk, a döntés a felhasználóé.
 */
@Component({
  selector: 'app-admin-atsorolas',
  templateUrl: './atsorolas.component.html',
  styleUrls: ['./atsorolas.component.scss'],
})
export class AdminAtsorolasComponent implements OnInit {
  intezmeny = '';
  betoltes = false;
  fut = false;
  hiba = '';

  osztalyok: any[] = [];
  tanulok: any[] = [];
  tanuloSzuro = '';

  valasztottTanulo: any = null;
  celOsztaly = '';
  datum = new Date().toISOString().slice(0, 10);

  elemzes: any = null;
  /** Csoportonkénti döntés: forrás csoport id -> cél csoport id ('' = nem sorolom át). */
  dontes: Record<string, string> = {};
  /** Csoportonként: lezárjuk-e a régi tagságot, ha nincs cél. */
  lezar: Record<string, boolean> = {};

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const si = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = String(si?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    await this.alapokBetoltese();
  }

  get intezmenyPath(): string { return encodeURI(this.intezmeny); }

  async alapokBetoltese() {
    this.betoltes = true;
    this.hiba = '';
    try {
      const [osztalyok, tanulok]: any[] = await Promise.all([
        firstValueFrom(this.http.get<any[]>(`/api/tanuloi-osztaly/osztalyok/${this.intezmenyPath}`)),
        firstValueFrom(this.http.get<any[]>(`/api/nyilvantartas/tanulo/${this.intezmenyPath}`)),
      ]);
      this.osztalyok = Array.isArray(osztalyok) ? osztalyok : [];
      this.tanulok = (Array.isArray(tanulok) ? tanulok : []).map((t: any) => ({
        oktazon: String(t.oktazon),
        nev: String(t.full_name || '').trim() || String(t.oktazon),
        osztaly: t.aktualis_osztaly || null,
      })).sort((a, b) => a.nev.localeCompare(b.nev, 'hu'));
    } catch (e: any) {
      this.hiba = e?.error?.error || 'Az alapadatok betöltése nem sikerült.';
    } finally {
      this.betoltes = false;
    }
  }

  get szurtTanulok(): any[] {
    const k = this.tanuloSzuro.trim().toLowerCase();
    if (!k) return this.tanulok.slice(0, 50);
    return this.tanulok
      .filter((t) => `${t.nev} ${t.oktazon} ${t.osztaly || ''}`.toLowerCase().includes(k))
      .slice(0, 50);
  }

  async tanuloValaszt(t: any) {
    this.valasztottTanulo = t;
    await this.elemez();
  }

  /*  Az elemzés a célosztály nélkül is fut: így már a választás ELŐTT látszik, milyen
   *  csoportokban van a tanuló, és melyik tartozik az osztályához. */
  async elemez() {
    if (!this.valasztottTanulo) { this.elemzes = null; return; }
    this.betoltes = true;
    this.hiba = '';
    try {
      const p = new URLSearchParams({ oktazon: this.valasztottTanulo.oktazon, datum: this.datum });
      if (this.celOsztaly) p.set('cel', this.celOsztaly);
      this.elemzes = await firstValueFrom(this.http.get(
        `/api/atsorolas/elemzes/${this.intezmenyPath}?${p.toString()}`));
      this.dontes = {};
      this.lezar = {};
      for (const cs of this.elemzes?.csoportok || []) {
        // Alapból csak azt kínáljuk fel, aminek EGYÉRTELMŰ párja van, és tényleg az
        // osztályhoz tartozik. A többihez nem nyúlunk kérdés nélkül.
        this.dontes[cs.id] = (cs.kapcsolat !== 'nincs' && cs.javasolt) ? cs.javasolt.id : '';
        this.lezar[cs.id] = cs.kapcsolat !== 'nincs' && !!cs.javasolt;
      }
    } catch (e: any) {
      this.hiba = e?.error?.error || 'Az elemzés nem sikerült.';
      this.elemzes = null;
    } finally {
      this.betoltes = false;
    }
  }

  kapcsolatSzoveg(cs: any): string {
    if (cs.kapcsolat === 'kotes') return 'az osztályhoz kötött csoport';
    if (cs.kapcsolat === 'nev') return 'a nevében szerepel az osztály';
    return 'nem az osztályhoz tartozik';
  }

  /** Mit fog csinálni a mentés ezzel a csoporttal – egy mondatban. */
  hatasSzoveg(cs: any): string {
    const cel = this.dontes[cs.id];
    if (cel) {
      const nev = this.csoportNev(cel);
      return `átsorolás ide: ${nev}`;
    }
    if (this.lezar[cs.id]) return 'kilép a csoportból (új tagság nélkül)';
    return 'marad, ahogy van';
  }

  csoportNev(id: string): string {
    for (const cs of this.elemzes?.csoportok || []) {
      if (cs.javasolt?.id === id) return cs.javasolt.name;
      const h = (cs.hasonlok || []).find((x: any) => x.id === id);
      if (h) return h.name;
    }
    return id;
  }

  get valtozoCsoport(): number {
    return (this.elemzes?.csoportok || [])
      .filter((cs: any) => this.dontes[cs.id] || this.lezar[cs.id]).length;
  }

  get celOsztalyNev(): string {
    return this.osztalyok.find((o) => String(o.id) === String(this.celOsztaly))?.osztaly || '';
  }

  async vegrehajt() {
    if (!this.valasztottTanulo || !this.celOsztaly) return;
    const csoportok = (this.elemzes?.csoportok || [])
      .filter((cs: any) => this.dontes[cs.id] || this.lezar[cs.id])
      .map((cs: any) => ({ forras: cs.id, cel: this.dontes[cs.id] || '' }));

    const sorok = [
      `${this.valasztottTanulo.nev} átsorolása: `
        + `${this.elemzes?.jelenlegi?.osztaly || 'nincs osztálya'} → ${this.celOsztalyNev}`,
      `Dátum: ${this.datum} (a régi besorolás az előző nappal zárul)`,
      csoportok.length ? `${csoportok.length} csoporttagság is változik.` : 'Csoporttagság nem változik.',
    ];
    if (!confirm(`${sorok.join('\n')}\n\nVégrehajtod?`)) return;

    this.fut = true;
    try {
      const r: any = await firstValueFrom(this.http.post(
        `/api/atsorolas/vegrehajt/${this.intezmenyPath}`,
        { oktazon: this.valasztottTanulo.oktazon, cel: this.celOsztaly, datum: this.datum, csoportok }));
      this.data.sendToast(
        `Átsorolva ide: ${r?.cel?.osztaly}. Csoport: ${r?.csoport_lezart || 0} lezárva, `
        + `${r?.csoport_uj || 0} új tagság.`, 'success');
      await this.alapokBetoltese();
      await this.elemez();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az átsorolás nem sikerült.', 'error');
    } finally {
      this.fut = false;
    }
  }
  /* ══════════════════════════════════════════════════════════════════════════
   *  HAMIS HIÁNYZÁS
   *
   *  A besorolás javítása a JÖVŐRE hat. Ami a rossz csoport óráin már be van
   *  jelölve, az ott marad — és a szülő joggal látja hamis mulasztásnak, mert a
   *  gyereke abba a csoportba sosem járt. (Élő eset: 9/ebfg/3/spanyol helyett a
   *  /2-be jár szeptember elseje óta.)
   *
   *  Hiányzást visszamenőleg törölni komoly döntés — a tanár tény-rögzítése —,
   *  ezért itt semmi nem történik magától: előbb LISTÁT adunk, és csak a
   *  kipipált órákról vesszük le a tanulót.
   * ═══════════════════════════════════════════════════════════════════════ */

  /** Melyik csoport óráit nézzük. Üres = nem nézünk semmit. */
  hamisCsoport = '';
  hamisTol = '';
  hamisIg = '';
  hamis: any = null;
  hamisFut = false;
  /** Óra-azonosítók, amiket a felhasználó kipipált. */
  hamisJelolt: Record<string, boolean> = {};

  get hamisJeloltDb(): number {
    return Object.values(this.hamisJelolt).filter(Boolean).length;
  }

  /*  Az összes kipipálása/levétele egy kattintással – 30 órát egyenként kipipálni
   *  büntetés, és épp az ilyen listánál hagy ki az ember egyet. */
  hamisMind(be: boolean) {
    for (const o of this.hamis?.orak || []) this.hamisJelolt[o.id] = be;
  }

  async hamisLeker() {
    if (!this.valasztottTanulo || !this.hamisCsoport) { this.hamis = null; return; }
    this.hamisFut = true;
    try {
      const p = new URLSearchParams({
        oktazon: this.valasztottTanulo.oktazon, csoport: this.hamisCsoport,
      });
      if (this.hamisTol) p.set('tol', this.hamisTol);
      if (this.hamisIg) p.set('ig', this.hamisIg);
      this.hamis = await firstValueFrom(this.http.get(
        `/api/atsorolas/hamis-hianyzas/${this.intezmenyPath}?${p.toString()}`));
      /*  Alapból SEMMI nincs kipipálva. A lista arra való, hogy megnézzék – nem
       *  arra, hogy egy elgépelt kattintás töröljön egy félév mulasztást. */
      this.hamisJelolt = {};
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A hiányzások lekérése nem sikerült.', 'error');
      this.hamis = null;
    } finally {
      this.hamisFut = false;
    }
  }

  hamisCsoportNev(): string {
    const cs = (this.elemzes?.csoportok || []).find((x: any) => String(x.id) === String(this.hamisCsoport));
    return cs?.name || '';
  }

  allapotSzoveg(o: any): string {
    if (o.igazolas_allapot === 'igazolt') return 'igazolt';
    if (o.igazolas_allapot === 'igazolatlan') return 'igazolatlan';
    return 'még nincs elbírálva';
  }

  async hamisTorol() {
    if (!this.valasztottTanulo || !this.hamisJeloltDb) return;
    const orak = (this.hamis?.orak || []).filter((o: any) => this.hamisJelolt[o.id]);
    const igazolt = orak.filter((o: any) => o.igazolas_allapot === 'igazolt').length;

    const sorok = [
      `${this.valasztottTanulo.nev}: ${orak.length} óra hiányzás-jelölése törlődik`,
      `a(z) „${this.hamisCsoportNev()}" csoport óráiról.`,
      igazolt ? `\nEbből ${igazolt} óra MÁR IGAZOLVA van — annak az igazolása is elvész.` : '',
      '\nA hiányzás TÉNYE tűnik el, nem csak az igazolása. Ezt csak akkor tedd meg,',
      'ha a tanuló valóban nem tartozott ebbe a csoportba.',
    ].filter(Boolean);
    if (!confirm(`${sorok.join('\n')}\n\nTörölhetem?`)) return;

    this.hamisFut = true;
    try {
      const r: any = await firstValueFrom(this.http.post(
        `/api/atsorolas/hamis-hianyzas/${this.intezmenyPath}`,
        { oktazon: this.valasztottTanulo.oktazon, orak: orak.map((o: any) => o.id) }));
      this.data.sendToast(`${r?.torolve || 0} óráról levéve a hiányzás.`, 'success');
      await this.hamisLeker();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A törlés nem sikerült.', 'error');
    } finally {
      this.hamisFut = false;
    }
  }
}
